/**
 * src/components/notes/NoteCard.jsx — a single note entry (root OR reply).
 *
 * Renders one note's body + meta and the per-entry affordances:
 *   - Archive (any authenticated user — collaborative feature).
 *   - Edit (AUTHOR only): swap the body for a textarea + Save/Cancel -> updateNote({id, body}).
 *   - Change-tag (AUTHOR only, ROOT only): the note_type chips become tappable ->
 *     updateNote({id, noteType, isDecision}); re-fetch moves a decision in/out of pinned.
 *
 * Author-gating is a UI affordance only — the server (api/data.js notes.update)
 * is the authority (403 on non-author). canEdit is passed in by the parent.
 *
 * Preserves the shipped design language: bolder card w/ soft rgba shadow, a 3px
 * accent rail + tinted badge on decisions, capitalised type pill. Mobile-first,
 * 44px touch targets, ACN design tokens only. data-testids preserved + extended.
 */

import { useState } from 'react';
import { StickyNote, Trash2, Star, Pencil, Check, X } from 'lucide-react';

// Soft maritime depth — rgba shadow is the sanctioned exception to the no-hex
// rule (overlays/shadows). Subtle, not a drop-shadow.
const CARD_SHADOW = '0 1px 2px rgba(0,0,0,0.05), 0 4px 14px rgba(0,0,0,0.04)';

// Tag chips offered when the author re-tags their own root note.
const TAG_CHIPS = [
  { key: 'note', labelKey: 'notes.typeNote' },
  { key: 'decision', labelKey: 'notes.typeDecision' },
  { key: 'risk', labelKey: 'notes.typeRisk' },
  { key: 'followup', labelKey: 'notes.typeFollowup' },
  { key: 'missing', labelKey: 'notes.typeMissing' },   // TICKET-735
  { key: 'reminder', labelKey: 'notes.typeReminder' }, // TICKET-735
];

const iconBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  minWidth: 44, minHeight: 44, padding: '8px 12px',
  background: 'transparent', color: 'var(--tm)',
  border: 'none', cursor: 'pointer', borderRadius: 8,
};

export default function NoteCard({
  note,
  canArchive,
  onArchive,
  canEdit = false,
  onEdit,
  onChangeTag,
  allowTagChange = false,
  reply = false,
  t,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body || '');

  // TICKET-CODEX-3371065407: pinned decisions are removed from the dated
  // timeline, so their card is the only place the date is visible. For
  // decisions older than today, show date + time so past pins are
  // distinguishable. Non-decision notes stay in a day-grouped section that
  // already provides the date label, so time-only is correct there.
  const whenRaw = note.created_at ? new Date(note.created_at) : null;
  const todayStart = whenRaw ? new Date().setHours(0, 0, 0, 0) : null;
  const noteStart = whenRaw ? new Date(whenRaw).setHours(0, 0, 0, 0) : null;
  const isOlderThanToday = whenRaw && noteStart < todayStart;
  const decision = !!note.is_decision;
  const when = whenRaw
    ? (decision && isOlderThanToday
        ? `${whenRaw.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${whenRaw.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
        : whenRaw.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
    : '';
  // "edited" indicator — the BEFORE UPDATE trigger bumps updated_at only on a
  // real edit (body or tag); archived notes are filtered out of the view, so a
  // visible note with updated_at meaningfully after created_at was genuinely
  // edited. (1s epsilon guards against insert-time rounding.)
  const createdMs = note.created_at ? new Date(note.created_at).getTime() : 0;
  const updatedMs = note.updated_at ? new Date(note.updated_at).getTime() : 0;
  const edited = createdMs > 0 && updatedMs > createdMs + 1000;
  const editedWhen = edited
    ? new Date(note.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';
  const author = note.author_name || note.author_role || '';
  const editable = !!canEdit;
  const tagChangeable = editable && allowTagChange && !reply;

  const startEdit = () => { setDraft(note.body || ''); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft(note.body || ''); };
  const saveEdit = async () => {
    const trimmed = draft.trim();
    if (trimmed.length === 0) return;
    // TICKET-CODEX-3371454210: skip the API call when the body is unchanged.
    // Decision: frontend guard is simpler than a DB trigger change and avoids a
    // spurious updated_at bump that would falsely show the "edited" indicator.
    // The DB trigger (set_notes_updated_at) fires on every UPDATE regardless;
    // the cheapest correct fix is to not make the call when nothing changed.
    if (trimmed === (note.body || '').trim()) { setEditing(false); return; }
    await onEdit?.(note.id, trimmed);
    setEditing(false);
  };

  return (
    <div
      data-testid={`note-card-${note.id}`}
      style={{
        background: 'var(--card)', borderRadius: reply ? 12 : 14, padding: reply ? 12 : 14,
        border: '1px solid var(--bdrg)',
        borderLeft: decision ? '3px solid var(--accent)' : '1px solid var(--bdrg)',
        boxShadow: reply ? 'none' : CARD_SHADOW,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {decision ? (
          <span
            data-testid={`note-decision-${note.id}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
              background: 'var(--sprob)', color: 'var(--accent)',
            }}
          >
            <Star size={10} /> {t('notes.decisionBadge')}
          </span>
        ) : (
          <StickyNote size={reply ? 12 : 14} color="var(--tm)" />
        )}

        {/* Tag chips: tappable when the author re-tags their own root; otherwise a static pill. */}
        {tagChangeable ? (
          TAG_CHIPS.map(({ key, labelKey }) => {
            const active = (note.note_type || 'note') === key || (key === 'decision' && decision);
            return (
              <button
                key={key}
                data-testid={`note-tag-${note.id}-${key}`}
                onClick={() => onChangeTag?.(note.id, key)}
                aria-pressed={active}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  minHeight: 44, padding: '6px 10px', borderRadius: 99,
                  fontSize: 10, fontWeight: active ? 700 : 500,
                  background: active ? 'var(--navy)' : 'var(--glass)',
                  color: active ? 'var(--card)' : 'var(--tm)',
                  border: '1px solid var(--bdr)', cursor: 'pointer', transition: 'all 120ms',
                }}
              >
                {key === 'decision' && <Star size={10} />}
                {t(labelKey)}
              </button>
            );
          })
        ) : (
          note.note_type && note.note_type !== 'note' && note.note_type !== 'decision' && (
            <span style={{
              fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
              background: 'var(--glass)', color: 'var(--tm)', border: '1px solid var(--bdr)',
              textTransform: 'capitalize',
            }}>
              {note.note_type}
            </span>
          )
        )}

        <span style={{ flex: 1 }} />
        <span
          style={{ fontSize: 11, color: 'var(--tm)' }}
          title={edited ? `${t('notes.edited')} · ${editedWhen}` : undefined}
        >
          {when}
          {edited && (
            <span data-testid={`note-edited-${note.id}`} style={{ fontStyle: 'italic' }}>
              {` · ${t('notes.edited')}`}
            </span>
          )}
        </span>

        {editable && !editing && (
          <button
            data-testid={`notes-edit-${note.id}`}
            onClick={startEdit}
            aria-label={t('notes.edit')}
            title={t('notes.edit')}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--accent)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--tm)'; }}
            style={iconBtn}
          >
            <Pencil size={14} />
          </button>
        )}

        {canArchive && !editing && (
          <button
            data-testid={`notes-archive-${note.id}`}
            onClick={() => onArchive(note.id)}
            aria-label={t('notes.archiveEntry')}
            title={t('notes.archiveEntry')}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--bad)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--tm)'; }}
            style={iconBtn}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <textarea
            data-testid={`notes-edit-input-${note.id}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            style={{
              width: '100%', padding: 10, borderRadius: 8,
              border: '1px solid var(--bdr)', background: 'var(--card)', color: 'var(--t1)',
              fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
              outline: 'none', minHeight: 72, lineHeight: 1.5,
            }}
          />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              data-testid={`notes-edit-cancel-${note.id}`}
              onClick={cancelEdit}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                minHeight: 44, padding: '8px 14px', borderRadius: 10,
                fontSize: 13, fontWeight: 600,
                background: 'var(--glass)', color: 'var(--tm)',
                border: '1px solid var(--bdr)', cursor: 'pointer',
              }}
            >
              <X size={14} />
              {t('notes.cancel')}
            </button>
            <button
              data-testid={`notes-edit-save-${note.id}`}
              onClick={saveEdit}
              disabled={draft.trim().length === 0}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                minHeight: 44, padding: '8px 16px', borderRadius: 10,
                fontSize: 13, fontWeight: 600,
                background: 'var(--navy-surface)', color: 'var(--on-navy)', border: 'none',
                cursor: draft.trim().length === 0 ? 'default' : 'pointer',
                opacity: draft.trim().length === 0 ? 0.5 : 1,
              }}
            >
              <Check size={14} />
              {t('notes.save')}
            </button>
          </div>
        </div>
      ) : (
        <div style={{
          fontSize: 14, color: 'var(--t1)', whiteSpace: 'pre-wrap', lineHeight: 1.55,
          fontWeight: decision ? 500 : 400,
        }}>
          {note.body}
        </div>
      )}

      {/* TICKET-735 — reminder due date; red once overdue */}
      {note.note_type === 'reminder' && note.due_at && (() => {
        const overdue = new Date(note.due_at).getTime() < Date.now();
        return (
          <div data-testid="note-due-at" data-overdue={overdue ? '1' : '0'} style={{
            fontSize: 11, fontWeight: 600,
            color: overdue ? 'var(--bad)' : 'var(--t2)',
          }}>
            {t('notes.dueAt')}: {new Date(note.due_at).toLocaleDateString()}
          </div>
        );
      })()}

      {author && <div style={{ fontSize: 11, color: 'var(--tm)' }}>{author}</div>}
    </div>
  );
}
